import React, { useState } from 'react';
import { 
  Upload,
  Brain,
  Microscope,
  BookOpen
} from 'lucide-react';

interface LabAnalysis {
  id: string;
  patientId: string;
  analysisType: 'single_result' | 'panel_analysis' | 'trend_analysis' | 'drug_interaction';
  results: {
    testName: string;
    value: number;
    unit: string;
    referenceRange: string;
    status: 'normal' | 'high' | 'low' | 'critical';
    flagReason?: string;
  }[];
  aiInsights: {
    clinicalSignificance: string;
    chemistryExplanation: string;
    associatedConditions: string[];
    recommendedFollowUp: string;
    educationalNotes: string;
    confidence: number;
  };
  correlations: {
    symptomCorrelations: Array<{symptom: string; correlation: number}>;
    medicationInteractions: Array<{medication: string; interaction: string}>;
    conditionMarkers: Array<{condition: string; likelihood: number}>;
  };
  created_at: string;
}

export const LabResultAnalyzer: React.FC = () => {
  const [analysisResults, setAnalysisResults] = useState<LabAnalysis[]>([
    {
      id: 'analysis-1',
      patientId: 'patient-1',
      analysisType: 'panel_analysis',
      results: [
        {
          testName: 'ALT',
          value: 180,
          unit: 'U/L',
          referenceRange: '7-56 U/L',
          status: 'high',
          flagReason: 'Significantly elevated - possible hepatotoxicity'
        },
        {
          testName: 'AST',
          value: 165,
          unit: 'U/L',
          referenceRange: '10-40 U/L',
          status: 'high',
          flagReason: 'Elevated with ALT - hepatocellular pattern'
        },
        {
          testName: 'Bilirubin Total',
          value: 3.2,
          unit: 'mg/dL',
          referenceRange: '0.3-1.2 mg/dL',
          status: 'high',
          flagReason: 'Elevated - suggests hepatic dysfunction'
        }
      ],
      aiInsights: {
        clinicalSignificance: 'Hepatocellular injury pattern with ALT/AST >3x upper limit. Total bilirubin elevation suggests impaired hepatic function.',
        chemistryExplanation: 'ALT (alanine aminotransferase) and AST (aspartate aminotransferase) are cytoplasmic enzymes released when hepatocytes are damaged. ALT is more liver-specific than AST.',
        associatedConditions: ['Drug-induced liver injury', 'Viral hepatitis', 'Autoimmune hepatitis', 'Ischemic hepatitis'],
        recommendedFollowUp: 'Immediate discontinuation of hepatotoxic medications. Check acetaminophen level, hepatitis panel, autoimmune markers.',
        educationalNotes: 'ALT/AST ratio <1 suggests alcoholic liver disease, >2 suggests drug-induced or viral hepatitis. Pattern recognition is key for differential diagnosis.',
        confidence: 94
      },
      correlations: {
        symptomCorrelations: [
          { symptom: 'Fatigue', correlation: 0.78 },
          { symptom: 'Nausea', correlation: 0.65 },
          { symptom: 'Right upper quadrant pain', correlation: 0.58 }
        ],
        medicationInteractions: [
          { medication: 'Acetaminophen', interaction: 'Hepatotoxic at high doses or with alcohol' },
          { medication: 'Statins', interaction: 'Can cause transaminase elevation' }
        ],
        conditionMarkers: [
          { condition: 'Drug-induced liver injury', likelihood: 85 },
          { condition: 'Viral hepatitis', likelihood: 72 },
          { condition: 'Autoimmune hepatitis', likelihood: 45 }
        ]
      },
      created_at: '2024-01-15T10:30:00Z'
    }
  ]);

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      // Here you would typically send the file to your AI analysis service
      analyzeLabFile(file);
    }
  };

  const analyzeLabFile = async (file: File) => {
    setIsAnalyzing(true);
    // Simulate AI analysis
    await new Promise(resolve => setTimeout(resolve, 3000));
    setIsAnalyzing(false);
    
    // Add new analysis result
    // This would be replaced with actual AI analysis results
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold text-slate-800">AI Lab Result Analyzer</h3>
          <p className="text-slate-600 mt-1">Upload lab results for advanced AI pattern analysis</p>
        </div>
      </div>

      {/* Upload Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
          <div className="flex flex-col items-center space-y-4">
            <div className="p-4 bg-blue-100 rounded-full">
              <Upload className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h4 className="text-lg font-semibold text-slate-800 mb-2">Upload Lab Results</h4>
              <p className="text-slate-600 mb-4">
                Upload lab reports, HL7 files, or structured lab data for AI analysis
              </p>
            </div>
            <input
              type="file"
              onChange={handleFileUpload}
              accept=".pdf,.txt,.hl7,.csv,.json"
              className="hidden"
              id="lab-upload"
            />
            <label
              htmlFor="lab-upload"
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg cursor-pointer transition-colors"
            >
              Choose File
            </label>
            <p className="text-xs text-slate-500">
              Supports: PDF, HL7, CSV, JSON formats
            </p>
          </div>
        </div>

        {isAnalyzing && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <Brain className="h-5 w-5 text-blue-600 animate-pulse" />
              <div>
                <p className="font-medium text-blue-800">AI Analysis in Progress...</p>
                <p className="text-sm text-blue-600">Analyzing patterns, correlations, and clinical significance</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Analysis Results */}
      {analysisResults.map((analysis) => (
        <div key={analysis.id} className="bg-white rounded-xl border border-slate-200">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Microscope className="h-6 w-6 text-blue-600" />
                <div>
                  <h4 className="text-lg font-semibold text-slate-800">Lab Panel Analysis</h4>
                  <p className="text-sm text-slate-600">Patient ID: {analysis.patientId}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                  {analysis.aiInsights.confidence}% confidence
                </span>
                <span className="text-sm text-slate-500">
                  {new Date(analysis.created_at).toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Lab Results */}
            <div>
              <h5 className="font-medium text-slate-800 mb-3">Lab Results</h5>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 text-sm font-medium text-slate-600">Test</th>
                      <th className="text-left py-2 text-sm font-medium text-slate-600">Result</th>
                      <th className="text-left py-2 text-sm font-medium text-slate-600">Reference Range</th>
                      <th className="text-left py-2 text-sm font-medium text-slate-600">Status</th>
                      <th className="text-left py-2 text-sm font-medium text-slate-600">Clinical Note</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {analysis.results.map((result, index) => (
                      <tr key={index}>
                        <td className="py-3 font-medium text-slate-800">{result.testName}</td>
                        <td className={`py-3 font-bold ${
                          result.status === 'critical' ? 'text-red-600' :
                          result.status === 'high' ? 'text-orange-600' :
                          result.status === 'low' ? 'text-blue-600' : 'text-green-600'
                        }`}>
                          {result.value} {result.unit}
                        </td>
                        <td className="py-3 text-slate-600">{result.referenceRange}</td>
                        <td className="py-3">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            result.status === 'critical' ? 'bg-red-100 text-red-700' :
                            result.status === 'high' ? 'bg-orange-100 text-orange-700' :
                            result.status === 'low' ? 'bg-blue-100 text-blue-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {result.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="py-3 text-sm text-slate-600">{result.flagReason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* AI Insights */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Brain className="h-5 w-5 text-purple-600" />
                <h5 className="font-medium text-purple-800">AI Clinical Analysis</h5>
              </div>
              
              <div className="space-y-4 text-sm">
                <div>
                  <h6 className="font-medium text-slate-800 mb-1">Clinical Significance:</h6>
                  <p className="text-slate-700">{analysis.aiInsights.clinicalSignificance}</p>
                </div>
                
                <div>
                  <h6 className="font-medium text-slate-800 mb-1 flex items-center space-x-1">
                    <BookOpen className="h-4 w-4" />
                    <span>Chemistry & Biochemistry:</span>
                  </h6>
                  <p className="text-slate-700 bg-white p-3 rounded border font-mono text-xs">
                    {analysis.aiInsights.chemistryExplanation}
                  </p>
                </div>
                
                <div>
                  <h6 className="font-medium text-slate-800 mb-1">Associated Conditions:</h6>
                  <div className="flex flex-wrap gap-2">
                    {analysis.aiInsights.associatedConditions.map((condition, index) => (
                      <span key={index} className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                        {condition}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h6 className="font-medium text-slate-800 mb-1">Recommended Follow-up:</h6>
                  <p className="text-slate-700 font-medium">{analysis.aiInsights.recommendedFollowUp}</p>
                </div>
                
                <div>
                  <h6 className="font-medium text-slate-800 mb-1">Educational Notes:</h6>
                  <p className="text-slate-600 italic">{analysis.aiInsights.educationalNotes}</p>
                </div>
              </div>
            </div>

            {/* Correlations */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 border border-slate-200 rounded-lg">
                <h6 className="font-medium text-slate-800 mb-2">Symptom Correlations</h6>
                <div className="space-y-2">
                  {analysis.correlations.symptomCorrelations.map((corr, index) => (
                    <div key={index} className="flex justify-between items-center text-sm">
                      <span className="text-slate-600">{corr.symptom}</span>
                      <span className="font-medium text-blue-600">{(corr.correlation * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-4 border border-slate-200 rounded-lg">
                <h6 className="font-medium text-slate-800 mb-2">Drug Interactions</h6>
                <div className="space-y-2">
                  {analysis.correlations.medicationInteractions.map((interaction, index) => (
                    <div key={index} className="text-sm">
                      <p className="font-medium text-slate-700">{interaction.medication}</p>
                      <p className="text-slate-600 text-xs">{interaction.interaction}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-4 border border-slate-200 rounded-lg">
                <h6 className="font-medium text-slate-800 mb-2">Condition Likelihood</h6>
                <div className="space-y-2">
                  {analysis.correlations.conditionMarkers.map((marker, index) => (
                    <div key={index} className="flex justify-between items-center text-sm">
                      <span className="text-slate-600">{marker.condition}</span>
                      <span className={`font-medium ${
                        marker.likelihood > 80 ? 'text-red-600' :
                        marker.likelihood > 60 ? 'text-orange-600' : 'text-yellow-600'
                      }`}>
                        {marker.likelihood}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
